import * as api from '@gitbook/api-client';
import {
    type PageCoverTextColor,
    type PageLayout,
    type PageLayoutOptionsCoverMask,
    type PageLayoutOptionsCoverSize,
    type PageLayoutOptionsWidth,
    type Revision,
    type RevisionFile,
    type RevisionPage,
    RevisionPageType,
    type RevisionReusableContent,
} from '@gitbook/hive-core';
import { safelyConvertEnum } from '@gitbook/util-ts';

/**
 * Convert an API revision to a Hive revision.
 */
export function apiToHive(input: {
    pages: api.RevisionPage[];
    files: api.RevisionFile[];
    reusableContents: api.RevisionReusableContent[];
}): Pick<Revision, 'rootPages' | 'pages' | 'files' | 'reusableContents'> {
    const files: Record<string, RevisionFile> = {};
    input.files.forEach((file) => {
        files[file.id] = convertAPIFileToHive(file);
    });

    const reusableContents: Record<string, RevisionReusableContent> = {};
    input.reusableContents.forEach((reusableContent) => {
        reusableContents[reusableContent.id] =
            convertAPIRevisionReusableContentToHive(reusableContent);
    });

    return {
        files,
        reusableContents,
        ...convertAPIPagesToHive(input.pages),
    };
}

function convertAPIFileToHive(input: api.RevisionFile): RevisionFile {
    return {
        id: input.id,
        name: input.name,
        contentType: input.contentType,
        size: input.size,
        dimensions: input.dimensions,
        downloadURL: input.downloadURL,
        edits: {},
    };
}

function convertAPIRevisionReusableContentToHive(
    input: api.RevisionReusableContent
): RevisionReusableContent {
    return {
        id: input.id,
        title: input.title,
        edits: {},
        ...('document' in input ? { document: input.document } : {}),
        ...('computed' in input
            ? {
                  computed: {
                      source: input.computed,
                      seed: input.computedSeed,
                  },
              }
            : {}),
    };
}

function convertAPIPagesToHive(input: api.RevisionPage[]): Pick<Revision, 'rootPages' | 'pages'> {
    let pages: Record<string, RevisionPage> = {};
    const rootPages: string[] = [];

    input.forEach((page) => {
        rootPages.push(page.id);

        switch (page.type) {
            case api.RevisionPageType.Computed: {
                pages[page.id] = {
                    id: page.id,
                    title: page.title,
                    type: RevisionPageType.Computed,
                    computed: page.computed,
                    edits: {},
                };
                break;
            }
            case api.RevisionPageType.Link: {
                pages[page.id] = {
                    id: page.id,
                    title: page.title,
                    icon: page.icon,
                    emoji: page.emoji,
                    type: RevisionPageType.Link,
                    target: page.target,
                    edits: {},
                };
                break;
            }
            case api.RevisionPageType.Document: {
                const descendants = convertAPIPagesToHive(page.pages);
                pages = {
                    ...pages,
                    ...descendants.pages,
                };

                pages[page.id] = {
                    id: page.id,
                    title: page.title,
                    slug: page.slug,
                    linkTitle: page.linkTitle,
                    tagTitle: page.tagTitle,
                    description: page.description,
                    icon: page.icon,
                    emoji: page.emoji,
                    hidden: page.hidden,
                    noIndex: page.noIndex,
                    noRobotsIndex: page.noRobotsIndex,
                    type: RevisionPageType.Document,
                    pages: descendants.rootPages,
                    ...('documentId' in page ? { document: page.documentId } : {}),
                    computed:
                        'computed' in page
                            ? {
                                  source: page.computed,
                                  seed: page.computedSeed,
                              }
                            : undefined,
                    edits: {},
                    variables: page.variables,
                    cover: page.cover
                        ? {
                              ...page.cover,
                              textColor: page.cover.textColor
                                  ? safelyConvertEnum<
                                        api.RevisionPageDocumentCoverTextColor,
                                        PageCoverTextColor
                                    >(page.cover.textColor)
                                  : undefined,
                              textColorDark: page.cover.textColorDark
                                  ? safelyConvertEnum<
                                        api.RevisionPageDocumentCoverTextColor,
                                        PageCoverTextColor
                                    >(page.cover.textColorDark)
                                  : undefined,
                          }
                        : undefined,
                    layout: convertAPIPageLayoutToHive(page.layout),
                };
                break;
            }
            case api.RevisionPageType.Group: {
                const descendants = convertAPIPagesToHive(page.pages);
                pages = {
                    ...pages,
                    ...descendants.pages,
                };

                pages[page.id] = {
                    id: page.id,
                    title: page.title,
                    slug: page.slug,
                    icon: page.icon,
                    emoji: page.emoji,
                    hidden: page.hidden,
                    type: RevisionPageType.Group,
                    pages: descendants.rootPages,
                    edits: {},
                };
                break;
            }
            default:
                break;
        }
    });

    return {
        rootPages,
        pages,
    };
}

function convertAPIPageLayoutToHive(input: api.RevisionPageLayoutOptions): PageLayout {
    return {
        ...input,
        coverMask: safelyConvertEnum<
            api.RevisionPageLayoutOptionsCoverMask,
            PageLayoutOptionsCoverMask
        >(input.coverMask),
        coverSize: safelyConvertEnum<
            api.RevisionPageLayoutOptionsCoverSize,
            PageLayoutOptionsCoverSize
        >(input.coverSize),
        width: safelyConvertEnum<api.RevisionPageLayoutOptionsWidth, PageLayoutOptionsWidth>(
            input.width
        ),
    };
}
